import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarHeart, Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const PILLARS = [["health", "Health"], ["body", "Body"], ["confidence", "Confidence"], ["routines", "Routines"], ["lifestyle", "Lifestyle"]];
const blank = { title: "", pillar: "health", action: "", minutes: 10, target: 12, project_key: null };
const field = "w-full h-11 px-3 rounded-xl border border-[#EFECE6] bg-white text-base";

function GoalEditor({ g, i, set, remove }) {
  return (
    <div className="rounded-2xl border border-[#EFECE6] p-4 space-y-2.5" data-testid={`month-goal-${i}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-[#C86D51]">Goal {i + 1}</span>
        <button onClick={remove} aria-label="Remove goal" data-testid={`month-goal-remove-${i}`} className="h-9 w-9 grid place-items-center text-[#8C857B]"><X className="h-4 w-4" /></button>
      </div>
      <input value={g.title} onChange={(e) => set({ title: e.target.value })} maxLength={80} placeholder="This month I want to…" data-testid={`month-goal-title-${i}`} className={field} />
      <input value={g.action} onChange={(e) => set({ action: e.target.value })} maxLength={80} placeholder="The daily action that moves it" data-testid={`month-goal-action-${i}`} className={field} />
      <div className="flex flex-wrap gap-1.5">{PILLARS.map(([k, l]) => <button key={k} onClick={() => set({ pillar: k })} data-testid={`month-goal-pillar-${i}-${k}`}
        className={`chip px-3 py-2 rounded-full text-xs border ${g.pillar === k ? "bg-stone-900 text-white border-stone-900" : "border-stone-200"}`}>{l}</button>)}</div>
      <label className="flex items-center gap-2 text-sm text-[#635E59]">Aim for
        <input type="number" min={1} max={31} value={g.target} onChange={(e) => set({ target: Math.max(1, Math.min(31, Number(e.target.value) || 1)) })} data-testid={`month-goal-target-${i}`} className="w-20 h-11 px-3 rounded-xl border border-[#EFECE6] text-base" />
        days this month</label>
      {g.why && <p className="text-xs text-[#8C857B]">{g.why}</p>}
    </div>
  );
}

export function MonthSetup({ open, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [goals, setGoals] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/v2/goals").then(({ data }) => { setD(data); setGoals(data.goals.length ? data.goals : data.proposals); }).catch((e) => toast.error(errMsg(e)));
  }, [open]);
  const set = (i, patch) => setGoals(goals.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  const save = async () => {
    const clean = goals.filter((g) => g.title.trim() && g.action.trim()).map(({ id, title, pillar, action, minutes, target, project_key }) => ({ id, title: title.trim(), pillar, action: action.trim(), minutes, target, project_key }));
    if (!clean.length) return toast.error("Add at least one goal with a daily action.");
    if (new Set(clean.map((g) => g.title.toLowerCase())).size !== clean.length) return toast.error("Two goals have the same title.");
    setBusy(true);
    try { await api.put("/v2/goals", { goals: clean }); toast.success(`${d.month_label} is set.`); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-lg overflow-y-auto overflow-x-hidden rounded-2xl [&>*]:min-w-0" data-testid="month-setup-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Set {d?.month_label || "this month"}</DialogTitle>
          <DialogDescription>Up to 3 goals. Each one gets a daily action, so every must-do shows what it moves.</DialogDescription>
        </DialogHeader>
        {d && <div className="space-y-3">
          {d.vision_line && <p className="text-sm text-[#635E59] italic" data-testid="month-setup-vision">"{d.vision_line}"</p>}
          {goals.map((g, i) => <GoalEditor key={g.id || i} g={g} i={i} set={(p) => set(i, p)} remove={() => setGoals(goals.filter((_, j) => j !== i))} />)}
          {goals.length < 3 && <button onClick={() => setGoals([...goals, { ...blank }])} data-testid="month-goal-add" className="w-full h-11 rounded-xl border border-dashed border-stone-300 text-sm font-semibold text-[#635E59] inline-flex items-center justify-center gap-2"><Plus className="h-4 w-4" />Add a goal</button>}
          <button disabled={busy} onClick={save} data-testid="month-setup-save" className="btn-ink w-full h-12 rounded-full font-semibold disabled:opacity-50">Save {d.month_label}</button>
        </div>}
      </DialogContent>
    </Dialog>
  );
}

export function MonthPrompt({ board, onOpen, onKept }) {
  const carried = board.goals.some((g) => g.carried);
  const keep = async () => { try { await api.post("/v2/goals/keep"); onKept(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="card p-5 bg-[#FAF4E8] border-[#F1E6CF] rise" data-testid="month-prompt">
      <span className="eyebrow flex items-center gap-1.5 !text-[#8A5A16]"><CalendarHeart className="h-3.5 w-3.5" />A new month</span>
      <p className="font-display text-xl mt-2 leading-snug">Set {board.month_label}: 60 seconds, up to 3 goals.</p>
      <p className="text-sm text-[#635E59] mt-1">CAMRYN has suggestions from your vision and projects. Keep, edit or remove them.</p>
      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={onOpen} data-testid="month-prompt-open" className="h-11 px-5 rounded-full bg-stone-900 text-white text-sm font-semibold">Set {board.month_label}</button>
        {carried && <button onClick={keep} data-testid="month-prompt-keep" className="h-11 px-5 rounded-full border border-stone-300 text-sm font-semibold">Keep last month's</button>}
      </div>
    </div>
  );
}
